import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TOUCH_TARGET_PX } from '../../config/touch-target';

interface SignatureRequestSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Cosa cambia la scelta in questo dialog (creazione, modifica, solo questo file). */
  helper: string;
  testId: string;
  /**
   * L'azienda non usa Prospero (nessuno firma dall'app): la firma non si può chiedere. Una
   * richiesta già presente resta e si può solo togliere.
   */
  isUnavailable?: boolean;
  /** Avviso informativo sotto lo switch, ad esempio con più clienti di cui solo alcuni firmano. */
  note?: string;
}

function SwitchNotice({ id, text, testId }: { id: string; text: string; testId: string }) {
  return (
    <Box
      id={id}
      data-testid={testId}
      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, color: 'info.dark' }}
    >
      <InfoOutlinedIcon aria-hidden fontSize="small" sx={{ mt: '1px' }} />
      <Typography variant="body2" sx={{ color: 'text.primary' }}>
        {text}
      </Typography>
    </Box>
  );
}

/** "Richiedi la firma dell'azienda": scelta del consulente, il documento lo firma un membro del cliente. */
export function SignatureRequestSwitch({
  checked,
  onChange,
  helper,
  testId,
  isUnavailable = false,
  note,
}: SignatureRequestSwitchProps) {
  const { t } = useTranslation();
  const helperId = `${testId}-helper`;
  const noticeId = `${testId}-notice`;
  const noticeText = isUnavailable
    ? t('consultant.deadlines.signature.unavailable', {
        defaultValue:
          "Questa azienda non usa Prospero: nessuno può firmare dall'app. Puoi chiedere la firma solo alle aziende con un account attivo.",
      })
    : note;
  const describedBy = noticeText ? `${noticeId} ${helperId}` : helperId;
  return (
    <Stack spacing={0.5}>
      <FormControlLabel
        sx={{ minHeight: TOUCH_TARGET_PX, ml: 0, mr: 0, gap: 1 }}
        disabled={isUnavailable && !checked}
        control={
          <Switch
            checked={checked}
            onChange={(_event, nextChecked) => onChange(nextChecked)}
            data-testid={testId}
            slotProps={{ input: { 'aria-describedby': describedBy } }}
          />
        }
        label={t('consultant.deadlines.signature.requestLabel', {
          defaultValue: "Richiedi la firma dell'azienda",
        })}
      />
      {noticeText && <SwitchNotice id={noticeId} text={noticeText} testId={noticeId} />}
      <Typography id={helperId} variant="caption" sx={{ color: 'text.secondary' }}>
        {helper}
      </Typography>
    </Stack>
  );
}
